// S3 Service for handling guest photos
class S3Service {
  /**
   * Check if a string is an S3 URL
   * @param {string} url - The URL to check
   * @returns {boolean} - True if it's an S3 URL
   */
  isS3Url(url) {
    if (!url || typeof url !== 'string') return false;
    return url.includes('amazonaws.com') || url.includes('s3://');
  }

  /**
   * Check if a string is a base64 data URL
   * @param {string} data - The data to check
   * @returns {boolean} - True if it's a base64 data URL
   */
  isBase64DataUrl(data) {
    if (!data || typeof data !== 'string') return false;
    return data.startsWith('data:image/');
  }

  /**
   * Get photo display URL - returns S3 URL directly or base64 as-is
   * Note: S3 URLs are already public-readable for this use case
   * @param {string} photoData - The photo data (S3 URL or base64)
   * @returns {string} - The display URL
   */
  getPhotoDisplayUrl(photoData) {
    if (!photoData) return null;
    
    if (this.isS3Url(photoData)) {
      // Return S3 URL directly (bucket is configured for public read)
      return photoData;
    } else if (this.isBase64DataUrl(photoData)) {
      // Return base64 data URL as-is
      return photoData;
    }
    
    return photoData; // Return as-is for other formats
  }

  /**
   * Get photo type for display purposes
   * @param {string} photoData - The photo data
   * @returns {string} - 'S3', 'base64', or 'unknown'
   */
  getPhotoType(photoData) {
    if (!photoData) return 'none';
    
    if (this.isS3Url(photoData)) {
      return 'S3';
    } else if (this.isBase64DataUrl(photoData)) {
      return 'base64';
    }
    
    return 'unknown';
  }

  /**
   * Get photo status for UI display
   * @param {string} photoData - The photo data
   * @returns {object} - Status object with type, uploaded, and displayText
   */
  getPhotoStatus(photoData) {
    const type = this.getPhotoType(photoData);
    
    switch (type) {
      case 'S3':
        return {
          type: 'S3',
          uploaded: true,
          displayText: 'Photo uploaded to S3 ✅',
          source: 'S3 Storage'
        };
      case 'base64':
        return {
          type: 'base64',
          uploaded: false,
          displayText: 'Photo captured (pending upload)',
          source: 'Local Data'
        };
      case 'none':
        return {
          type: 'none',
          uploaded: false,
          displayText: 'No photo provided',
          source: 'None'
        };
      default:
        return {
          type: 'unknown',
          uploaded: false,
          displayText: 'Unknown photo format',
          source: 'Unknown'
        };
    }
  }

  /**
   * Convert blob to base64 data URL
   * @param {Blob} blob - The blob to convert
   * @returns {Promise<string>} - The base64 data URL
   */
  blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Extract filename from S3 URL
   * @param {string} s3Url - The S3 URL
   * @returns {string} - The filename
   */
  extractFilename(s3Url) {
    if (!this.isS3Url(s3Url)) return null;
    
    try {
      const url = new URL(s3Url);
      const pathname = url.pathname;
      return pathname.split('/').pop();
    } catch (e) {
      return null;
    }
  }

  /**
   * Get photo metadata from S3 URL
   * @param {string} s3Url - The S3 URL
   * @returns {object} - Metadata object
   */
  getPhotoMetadata(s3Url) {
    if (!this.isS3Url(s3Url)) return null;
    
    const filename = this.extractFilename(s3Url);
    if (!filename) return null;
    
    // Parse filename format: guest-{index}-{timestamp}-{uuid}.jpg
    const parts = filename.replace('.jpg', '').split('-');
    if (parts.length >= 4) {
      return {
        guestIndex: parts[1],
        timestamp: parts[2],
        uuid: parts[3],
        filename: filename
      };
    }
    
    return { filename };
  }
}

export default new S3Service();
